import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Instagram, Mail, Phone, MapPin } from 'lucide-react';
import TKFIconWhite from '../icons/TKFIcon-White';
import { useSiteContent } from '../../hooks/useSiteContent';

// TikTok Icon Component
const TikTokIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FooterContainer = styled.footer`
  background-color: ${props => props.theme.colors.primary[900]};
  color: ${props => props.theme.colors.neutral[400]};
  padding: ${props => props.theme.sectionPadding.md} 0 ${props => props.theme.spacing[8]};
`;

const FooterContent = styled.div`
  max-width: ${props => props.theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing[5]};

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    padding: 0 ${props => props.theme.spacing[8]};
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    padding: 0 ${props => props.theme.spacing[10]};
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing[10]};

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1.5fr 1fr 1fr 1.4fr;
    gap: ${props => props.theme.spacing[12]};
  }
`;

const FooterSection = styled.div`
  h3 {
    font-family: ${props => props.theme.typography.fontFamily.body};
    font-size: ${props => props.theme.typography.fontSize.xs};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
    text-transform: uppercase;
    margin-bottom: ${props => props.theme.spacing[5]};
    color: ${props => props.theme.colors.neutral[500]};
  }

  ul {
    list-style: none;
  }

  li {
    margin-bottom: ${props => props.theme.spacing[3]};
  }

  a {
    color: ${props => props.theme.colors.neutral[300]};
    text-decoration: none;
    transition: color ${props => props.theme.animation.duration.fast} ${props => props.theme.animation.easing.easeOut};

    &:hover {
      color: ${props => props.theme.colors.white};
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  margin-bottom: ${props => props.theme.spacing[3]};
  color: ${props => props.theme.colors.neutral[300]};

  svg {
    color: ${props => props.theme.colors.primary[400]};
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[3]};
  margin-top: ${props => props.theme.spacing[6]};

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: ${props => props.theme.borderRadius.minimal};
    transition: background-color ${props => props.theme.animation.duration.fast} ${props => props.theme.animation.easing.easeOut},
      border-color ${props => props.theme.animation.duration.fast} ${props => props.theme.animation.easing.easeOut};

    &:hover {
      background-color: ${props => props.theme.colors.primary[500]};
      border-color: ${props => props.theme.colors.primary[500]};
    }

    svg {
      color: white;
      width: 18px;
      height: 18px;
    }
  }
`;

const FooterBottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  margin-top: ${props => props.theme.spacing[16]};
  padding-top: ${props => props.theme.spacing[6]};
  color: ${props => props.theme.colors.neutral[500]};
  font-size: ${props => props.theme.typography.fontSize.sm};

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const PoweredBy = styled.a`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.neutral[500]};
  text-decoration: none;

  &:hover {
    color: ${props => props.theme.colors.neutral[300]};
  }
`;

const FooterTagline = styled.p`
  color: ${props => props.theme.colors.neutral[300]};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
  max-width: 38ch;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  margin-bottom: ${props => props.theme.spacing[5]};

  div {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  span {
    font-family: ${props => props.theme.typography.fontFamily.heading};
    font-size: ${props => props.theme.typography.fontSize.xl};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
    color: white;
  }
`;

const Footer: React.FC = () => {
  const { siteContent } = useSiteContent();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <Logo>
              <div>
                <TKFIconWhite size={36} />
              </div>
              <span>TKF</span>
            </Logo>
            <FooterTagline>
              {siteContent?.footer.description || "Dedicated to making a positive impact in communities through sustainable programs and compassionate support."}
            </FooterTagline>
            <SocialLinks>
              {siteContent?.contactInfo.socialLinks.instagram && (
                <a href={siteContent.contactInfo.socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                  <Instagram size={20} />
                </a>
              )}
              <a href="https://www.tiktok.com/@kuddyfoundation" target="_blank" rel="noopener noreferrer">
                <TikTokIcon size={20} />
              </a>
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <h3>Quick links</h3>
            <ul>
              <li><Link to="/about-us">About Us</Link></li>
              <li><Link to="/initiatives">Our Causes</Link></li>
            </ul>
          </FooterSection>

          <FooterSection>
            <h3>Get involved</h3>
            <ul>
              <li><Link to="/initiatives">Make a Donation</Link></li>
            </ul>
          </FooterSection>

          <FooterSection>
            <h3>Contact</h3>
            <ContactItem>
              <Mail size={16} />
              <span>{siteContent?.contactInfo.email || "info@thekuddyfoundation.org"}</span>
            </ContactItem>
            <ContactItem>
              <Phone size={16} />
              <span>{siteContent?.contactInfo.phone || "+1 (548) 390-4250"}</span>
            </ContactItem>
            <ContactItem>
              <MapPin size={16} />
              <span>{siteContent?.contactInfo.address || "Brampton, Ontario, Canada"}</span>
            </ContactItem>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <p>
            &copy; {new Date().getFullYear()} {siteContent?.footer.copyright || "The Kuddy Foundation. All rights reserved."}
          </p>
          <PoweredBy href={siteContent?.footer.poweredBy.url || "https://nexiera.ca"} target="_blank" rel="noopener noreferrer">
            {siteContent?.footer.poweredBy.text || "Powered by NXE"}
          </PoweredBy>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;